import axios from 'axios'
import { isObject } from 'lodash-es'
import { ReactNode } from 'react'
import { toast } from 'react-toastify'

import { NSKey } from '@src/i18n'
import { ApiProblemDetails } from '@src/models/api'
import { tStable } from '@src/utils/translate/tStable'

export const errorUtils = {
    getNetworkFriendlyErrorMessage: (error: any) => {
        console.log('--errorUtils.getNetworkFriendlyErrorMessage', error)
        if (error?.response) {
            // The request was made and the server responded with a invoiceStatusCode code
            // that falls out of the range of 2xx
            return error.response?.status
        } else if (error?.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            return error.request?.statusText || error?.message
        } else {
            // Error text
            if (typeof error === 'string') {
                return error
            }

            // Something happened in setting up the request that triggered an Error
            return error?.message
        }
    }
}

type ValidationPrimitive = string | number | boolean | bigint | symbol | null | undefined

// Keys ASP.NET emits when a failed field is an array element: `field[0]`, and
// for arrays of objects also the per-property `field[0].subField`.
type ElementErrorKeys<K extends string, E> = NonNullable<E> extends ValidationPrimitive
    ? `${K}[${number}]`
    : `${K}[${number}]` | `${K}[${number}].${keyof NonNullable<E> & string}`

// Given a field K of value type V, the error keys it can produce: the field
// name itself, plus indexed element keys when V is an array.
type ErrorKeys<K extends string, V> = NonNullable<V> extends readonly (infer E)[] ? K | ElementErrorKeys<K, E> : K

// Maps a domain model to its validation-errors shape: every field becomes an
// optional array of messages, and array fields additionally expose indexed
// element keys, e.g.
//   { areas: Area[]; startHour: number }
//     -> { startHour?: string[]; areas?: string[]; `areas[${number}]`?: string[]; `areas[${number}].title`?: string[]; ... }
export type ValidationErrors<T> = {
    [K in keyof T as ErrorKeys<K & string, T[K]>]?: string[]
}

interface IHttpErrorDetail<Problem, Result, Errors> {
    title: string
    detail: string
    status: number
    problemDetails: Problem | null
    result: Result | null
    errors: ValidationErrors<Errors> | null
}

// Normalizes the response body out of the two error shapes that reach us:
//  - axios error:        { response: { data } }
//  - RTK Query error:    { data }   (see rtkBaseQuery)
const getErrorBody = (error: unknown): Record<string, any> | null => {
    if (!isObject(error)) {
        return null
    }

    if ('response' in error && isObject(error.response) && 'data' in error.response && isObject(error.response.data)) {
        return error.response.data as Record<string, any>
    }

    if ('data' in error && isObject(error.data)) {
        return error.data as Record<string, any>
    }

    return null
}

// Status can live in different places depending on the error shape:
//  - axios error:        response.status (or request.status)
//  - RTK Query error:    status
const getErrorStatus = (error: unknown): number => {
    if (!isObject(error)) {
        return 0
    }

    if ('response' in error && isObject(error.response) && 'status' in error.response && error.response.status) {
        return Number(error.response.status)
    }

    if ('status' in error) {
        return Number(error.status)
    }

    if ('request' in error && isObject(error.request) && 'status' in error.request) {
        return Number(error.request.status)
    }

    return 0
}

export const parseHttpError = <Problem = ApiProblemDetails, Result = unknown, Errors = Record<string, unknown>>(
    error: unknown
): IHttpErrorDetail<Problem, Result, Errors> => {
    const status = getErrorStatus(error)
    const body = getErrorBody(error)
    const extensions = body && isObject(body.Extensions) ? body.Extensions : null

    return {
        status,
        title: body && 'Title' in body ? String(body.Title) : '',
        detail: body && 'Detail' in body ? String(body.Detail) : '',
        errors: extensions && 'errors' in extensions ? (extensions.errors as ValidationErrors<Errors>) : null,
        result: extensions && 'result' in extensions ? (extensions.result as Result) : null,
        // For 400 the body is guaranteed to be ApiProblemDetails (or a derivative); tighten it.
        problemDetails: status === 400 ? (body as Problem) : null
    }
}

interface IHandleHttpErrorOptions {
    show403Error?: boolean
}

// A fixed toastId keeps it to one toast when several failed requests, or the API middleware and a caller, show it
export const showNetworkErrorToast = () => {
    toast.error(tStable(NSKey.Common, 'errors.noConnection'), { toastId: 'network-error' })
}

export const handleHttpError = (error: unknown, message?: ReactNode, options: IHandleHttpErrorOptions = {}) => {
    if (isNetworkError(error)) {
        showNetworkErrorToast()
        return
    }

    const status = getErrorStatus(error)

    if (status === 403 && options.show403Error) {
        toast.error(tStable(NSKey.Common, 'errors.accessDenied'))
        return
    } else if (status === 403) {
        // Ignore 403 error since it is handled in RTK Query baseQuery
        return
    }

    toast.error(message ?? tStable(NSKey.Common, 'errors.somethingGoesWrong'))
}

export const isAbortError = (error: unknown) => {
    return isObject(error) && 'name' in error && error.name === 'AbortError'
}

export const isRequestError = (error: unknown) => {
    if (axios.isCancel(error) || isAbortError(error)) {
        return true
    }

    if (!isObject(error)) {
        return false
    }

    return 'config' in error || 'response' in error || 'request' in error || 'status' in error
}

export const isNetworkError = (error: unknown) => {
    if (axios.isCancel(error) || !isObject(error)) {
        return false
    }

    // RTK Query error, see rtkBaseQuery
    if ('isNetworkError' in error) {
        return error.isNetworkError === true
    }

    return 'config' in error && !('response' in error && error.response)
}
